import {formatTimestamp} from "../../../utils/TableTimestamps.tsx";
import {DataTableColumn} from "mantine-datatable";
import {NavLink} from "react-router-dom";
import {Execution} from "../types.ts";
import {I18n} from "@aws-amplify/core";
import {ExecutionStatusBadge} from "../components/ExecutionStatusBadge.tsx";
import {Text} from "@mantine/core";

export const useExecutionsTableColumns = () => {

    // Every column is sortable except `progress`, which is called out below.
    // Sorting is client-side over the pages loaded so far — see useExecutions.
    const columns: DataTableColumn<Execution>[] = [
        {
            accessor: 'name',
            title: I18n.get('Name'),
            width: '1fr',
            sortable: true,
            render: (item: Execution) => <NavLink to={`/executions/${item.id}`}>{item.name ?? item.id}</NavLink>,
        },
        {
            accessor: 'playbook',
            title: I18n.get('Playbook'),
            width: '1fr',
            sortable: true,
            render: (item: Execution) => item.playbookId ? (
                <NavLink to={`/playbooks/${item.playbookId}`}>
                    {item.playbook?.displayName ?? item.playbook?.name ?? item.playbookId}
                </NavLink>
            ) : <Text size={'sm'}>n/a</Text>,
        },
        {
            accessor: 'executionStatus',
            title: I18n.get('Status'),
            width: '120px',
            sortable: true,
            render: (item: Execution) => <ExecutionStatusBadge status={item.executionStatus}/>,
        },
        {
            // NOT sortable, deliberately. `progress` has no backing field: it
            // is rendered from two of them, so there is nothing for the
            // comparator to read and a header here would look clickable and do
            // nothing — strictly worse than no sorting (§3a). Sort by Status
            // to group running executions instead.
            accessor: 'progress',
            title: I18n.get('Progress'),
            width: '120px',
            render: (item: Execution) => (
                <Text size={'sm'}>
                    {item.completedPhases ?? 0} / {item.totalPhases ?? 0}
                </Text>
            ),
        },
        {
            accessor: 'startedAt',
            title: I18n.get('Started'),
            width: '1fr',
            sortable: true,
            render: (item: Execution) => item.startedAt ? formatTimestamp(item.startedAt) : <Text size={'sm'}>n/a</Text>,
        },
        {
            // The default sort key, so the order the table opens in is one the
            // operator can see and re-apply. `startedAt` is null while an
            // execution is PENDING; `created` is not — see useExecutions.
            accessor: 'created',
            title: I18n.get('Submitted'),
            width: '1fr',
            sortable: true,
            render: (item: Execution) => item.created ? formatTimestamp(item.created) : <Text size={'sm'}>n/a</Text>,
        },
        {
            accessor: 'createdByUsername',
            title: I18n.get('Started By'),
            width: '1fr',
            sortable: true,
            render: (item: Execution) => <Text size={'sm'}>{item.createdByUsername ?? 'n/a'}</Text>,
        },
    ];

    return {
        columns,
    };
};
